<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import type { AxiosError } from 'axios'

import SupportService from '@/services/support/supportService.ts'
import { useAuthStore } from '@/stores/authStore.ts'
import { displayResponseError, displayToast } from '@/utils/toastUtils.ts'

import type {
  SupportTicketDetail,
  TicketPriority,
  TicketStatus,
} from '@/types/services/support/support.type.ts'
import type { MeResponse } from '@/types/services/auth/auth.type.ts'
import type { ApiError } from '@/types/services/utils/axios.type.ts'

const props = defineProps<{ ticketId: string }>()

const { t } = useI18n()
const router = useRouter()
const authStore = useAuthStore()

const ticket = ref<SupportTicketDetail | null>(null)
const loading = ref<boolean>(false)
const tab = ref<'messages' | 'comments'>('messages')
const newMessage = ref<string>('')
const newComment = ref<string>('')
const sending = ref<boolean>(false)

const statusOptions = ['open', 'in_progress', 'resolved', 'closed'] as const
const priorityOptions = ['low', 'normal', 'high', 'urgent'] as const

const currentUserId = computed<number>(() => Number((authStore.user as MeResponse)?.id))
const isClosed = computed<boolean>(() => ticket.value?.status === 'closed')

function hasPermission(permission: string[]): boolean {
  return authStore.hasPermission(permission)
}

function userLabel(user: { firstName: string | null; lastName: string | null; email: string }): string {
  const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()
  return name || user.email
}

function formatDate(value: string): string {
  return new Date(value).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

async function fetchTicket(): Promise<void> {
  loading.value = true
  try {
    ticket.value = await SupportService.get(Number(props.ticketId))
  } catch (error) {
    displayResponseError(error as AxiosError<ApiError>)
  } finally {
    loading.value = false
  }
}

watch(() => props.ticketId, fetchTicket, { immediate: true })

async function updateField(payload: { status?: TicketStatus; priority?: TicketPriority; assigneeId?: number | null }): Promise<void> {
  if (!ticket.value) return
  try {
    ticket.value = await SupportService.update(ticket.value.id, payload)
    displayToast(t('views.support.toast.updated'), 'success')
  } catch (error) {
    displayResponseError(error as AxiosError<ApiError>)
    await fetchTicket()
  }
}

async function sendMessage(): Promise<void> {
  if (!ticket.value || newMessage.value.trim() === '') return
  sending.value = true
  try {
    const message = await SupportService.addMessage(ticket.value.id, newMessage.value.trim())
    ticket.value.messages.push(message)
    newMessage.value = ''
    displayToast(t('views.support.toast.messageSent'), 'success')
  } catch (error) {
    displayResponseError(error as AxiosError<ApiError>)
  } finally {
    sending.value = false
  }
}

async function sendComment(): Promise<void> {
  if (!ticket.value || newComment.value.trim() === '') return
  sending.value = true
  try {
    const comment = await SupportService.addComment(ticket.value.id, newComment.value.trim())
    ticket.value.comments.push(comment)
    newComment.value = ''
    displayToast(t('views.support.toast.commentAdded'), 'success')
  } catch (error) {
    displayResponseError(error as AxiosError<ApiError>)
  } finally {
    sending.value = false
  }
}
</script>

<template>
  <div class="pa-6">
    <v-btn variant="text" prepend-icon="PhArrowLeft" class="mb-4" @click="router.push({ name: 'supportTickets' })">
      {{ t('views.support.detail.back') }}
    </v-btn>

    <v-progress-linear v-if="loading" indeterminate color="secondary" />

    <template v-if="ticket">
      <div class="d-flex ga-6 flex-wrap">
        <!-- Main column -->
        <div style="flex: 1 1 520px; min-width: 320px">
          <h1 class="text-h5 font-weight-bold mb-1">{{ ticket.subject }}</h1>
          <p class="text-medium-emphasis mb-4">
            #{{ ticket.id }} · {{ t(`views.support.type.${ticket.type}`) }}
          </p>

          <v-tabs v-model="tab" color="secondary" class="mb-4">
            <v-tab value="messages">{{ t('views.support.detail.tabs.messages') }}</v-tab>
            <v-tab value="comments">{{ t('views.support.detail.tabs.comments') }}</v-tab>
          </v-tabs>

          <v-window v-model="tab">
            <!-- Messages -->
            <v-window-item value="messages">
              <div v-if="ticket.messages.length === 0" class="text-medium-emphasis py-4">
                {{ t('views.support.detail.messages.empty') }}
              </div>
              <div
                v-for="message in ticket.messages"
                :key="message.id"
                class="d-flex mb-3"
                :class="message.authorType === 'staff' ? 'justify-end' : 'justify-start'"
              >
                <v-sheet
                  :color="message.authorType === 'staff' ? 'secondary' : 'grey-lighten-3'"
                  :class="message.authorType === 'staff' ? 'text-white' : ''"
                  class="pa-3 rounded-lg"
                  style="max-width: 78%"
                >
                  <div class="text-caption font-weight-medium mb-1">
                    {{ message.authorType === 'staff' ? t('views.support.detail.messages.staff') : t('views.support.detail.messages.client') }}
                    · {{ userLabel(message.author) }}
                  </div>
                  <div style="white-space: pre-wrap">{{ message.content }}</div>
                  <div class="text-caption mt-1" style="opacity: 0.7">{{ formatDate(message.createdAt) }}</div>
                </v-sheet>
              </div>

              <v-alert v-if="isClosed" type="info" variant="tonal" density="compact" class="mt-3">
                {{ t('views.support.detail.messages.closedNotice') }}
              </v-alert>
              <div v-else-if="hasPermission(['MESSAGE_SUPPORT_TICKET'])" class="mt-3">
                <v-textarea
                  v-model="newMessage"
                  :placeholder="t('views.support.detail.messages.placeholder')"
                  variant="outlined"
                  rows="3"
                  auto-grow
                  hide-details
                  class="mb-2"
                />
                <div class="d-flex justify-end">
                  <v-btn
                    color="secondary"
                    prepend-icon="PhPaperPlaneTilt"
                    :disabled="newMessage.trim() === ''"
                    :loading="sending"
                    @click="sendMessage"
                  >
                    {{ t('views.support.detail.messages.send') }}
                  </v-btn>
                </div>
              </div>
            </v-window-item>

            <!-- Internal comments -->
            <v-window-item value="comments">
              <div v-if="ticket.comments.length === 0" class="text-medium-emphasis py-4">
                {{ t('views.support.detail.comments.empty') }}
              </div>
              <v-sheet
                v-for="comment in ticket.comments"
                :key="comment.id"
                color="amber-lighten-5"
                class="pa-3 rounded-lg mb-2"
              >
                <div class="text-caption font-weight-medium mb-1">
                  {{ userLabel(comment.author) }} · {{ formatDate(comment.createdAt) }}
                </div>
                <div style="white-space: pre-wrap">{{ comment.content }}</div>
              </v-sheet>

              <div v-if="hasPermission(['COMMENT_SUPPORT_TICKET'])" class="mt-3">
                <v-textarea
                  v-model="newComment"
                  :placeholder="t('views.support.detail.comments.placeholder')"
                  variant="outlined"
                  rows="3"
                  auto-grow
                  hide-details
                  class="mb-2"
                />
                <div class="d-flex justify-end">
                  <v-btn
                    color="secondary"
                    variant="tonal"
                    prepend-icon="PhNotePencil"
                    :disabled="newComment.trim() === ''"
                    :loading="sending"
                    @click="sendComment"
                  >
                    {{ t('views.support.detail.comments.add') }}
                  </v-btn>
                </div>
              </div>
            </v-window-item>
          </v-window>
        </div>

        <!-- Side panel -->
        <div style="flex: 0 0 280px">
          <v-card variant="outlined" class="pa-4">
            <div class="text-caption text-medium-emphasis">{{ t('views.support.detail.client') }}</div>
            <div class="mb-3 font-weight-medium">{{ userLabel(ticket.client) }}<br><span class="text-caption">{{ ticket.client.email }}</span></div>

            <v-select
              :model-value="ticket.status"
              :items="statusOptions.map((value) => ({ title: t(`views.support.status.${value}`), value }))"
              :label="t('views.support.detail.status')"
              :disabled="!hasPermission(['EDIT_SUPPORT_TICKET'])"
              variant="outlined"
              density="compact"
              hide-details
              class="mb-3"
              @update:model-value="(value: TicketStatus) => updateField({ status: value })"
            />

            <v-select
              :model-value="ticket.priority"
              :items="priorityOptions.map((value) => ({ title: t(`views.support.priority.${value}`), value }))"
              :label="t('views.support.detail.priority')"
              :disabled="!hasPermission(['EDIT_SUPPORT_TICKET'])"
              variant="outlined"
              density="compact"
              hide-details
              class="mb-3"
              @update:model-value="(value: TicketPriority) => updateField({ priority: value })"
            />

            <div class="text-caption text-medium-emphasis">{{ t('views.support.detail.assignee') }}</div>
            <div class="mb-2 font-weight-medium">
              {{ ticket.assignee ? userLabel(ticket.assignee) : t('views.support.detail.unassigned') }}
            </div>
            <template v-if="hasPermission(['EDIT_SUPPORT_TICKET'])">
              <v-btn
                v-if="ticket.assignee?.id !== currentUserId"
                size="small"
                variant="tonal"
                color="secondary"
                block
                class="mb-2"
                @click="updateField({ assigneeId: currentUserId })"
              >
                {{ t('views.support.detail.assignToMe') }}
              </v-btn>
              <v-btn
                v-if="ticket.assignee"
                size="small"
                variant="text"
                block
                @click="updateField({ assigneeId: null })"
              >
                {{ t('views.support.detail.unassign') }}
              </v-btn>
            </template>

            <v-divider class="my-3" />
            <div class="text-caption text-medium-emphasis">{{ t('views.support.detail.createdBy') }}</div>
            <div class="font-weight-medium mb-2">{{ userLabel(ticket.createdBy) }}</div>
            <div class="text-caption text-medium-emphasis">{{ t('views.support.detail.createdAt') }}</div>
            <div class="font-weight-medium">{{ formatDate(ticket.createdAt) }}</div>
          </v-card>
        </div>
      </div>
    </template>
  </div>
</template>
